import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Sparkles, Loader2, Eye,
  BookOpen, Users, Palette, Paintbrush, Heart, Check,
  UtensilsCrossed, Calculator, Music, Briefcase, CloudRain, LayoutGrid,
  Clock, Baby, Car, ClipboardList, ShieldCheck, Lock,
} from 'lucide-react';
import PremiumPaywall from '../components/PremiumPaywall';
import { CharacterBuilder } from '../components/customizer/CharacterBuilder';
import { StylePicker } from '../components/customizer/StylePicker';
import { ColorPalettePicker } from '../components/customizer/ColorPalettePicker';
import { GuestListBuilder } from '../components/customizer/GuestListBuilder';
import { BudgetTracker } from '../components/customizer/BudgetTracker';
import { MusicPlanner } from '../components/customizer/MusicPlanner';
import { MemorialSection } from '../components/customizer/MemorialSection';
import { VendorList } from '../components/customizer/VendorList';
import { FoodPlanner } from '../components/customizer/FoodPlanner';
import { PlanBSection } from '../components/customizer/PlanBSection';
import { ForgottenAnalyzer } from '../components/customizer/ForgottenAnalyzer';
import { SeatingChart } from '../components/customizer/SeatingChart';
import { MoodboardBuilder } from '../components/customizer/MoodboardBuilder';
import { FlowerPlanner } from '../components/customizer/FlowerPlanner';
import { StationeryPlanner } from '../components/customizer/StationeryPlanner';
import { AttirePlanner } from '../components/customizer/AttirePlanner';
import { PhotoChecklist } from '../components/customizer/PhotoChecklist';
import { CoupleSetup, getCoupleLabel, getPersonTitle, getSideLabel } from '../components/customizer/CoupleSetup';
import { BookDesignSelector } from '../components/customizer/BookDesignSelector';
import { WeddingTimeline } from '../components/customizer/WeddingTimeline';
import { KidsPlanner } from '../components/customizer/KidsPlanner';
import { CeremonyPlanner } from '../components/customizer/CeremonyPlanner';
import { TransportPlanner } from '../components/customizer/TransportPlanner';
import { ResponsibilityTracker } from '../components/customizer/ResponsibilityTracker';
import { EmergencyKit } from '../components/customizer/EmergencyKit';
import { DayBeforePlanner } from '../components/customizer/DayBeforePlanner';
import { WeddingMorningPlanner } from '../components/customizer/WeddingMorningPlanner';
import { AfterPartyPlanner } from '../components/customizer/AfterPartyPlanner';
import { HoneymoonPlanner } from '../components/customizer/HoneymoonPlanner';
import { LegalAdminPlanner } from '../components/customizer/LegalAdminPlanner';
import { PetPlanner } from '../components/customizer/PetPlanner';
import { getThemeById, weddingThemes as allWeddingThemes } from '../data/weddingThemes';
import {
  getCustomizationSchema, defaultCharacter,
  illustrationStyles, colorPalettes,
} from '../data/templateCustomizationSchemas';
import UserNavMenu from '../components/UserNavMenu';
import LumeraLogo from '../components/LumeraLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = { BookOpen, Users, Palette, Paintbrush, Eye, Sparkles, Heart, UtensilsCrossed, Calculator, Music, Briefcase, CloudRain, LayoutGrid, Clock, Baby, Car, ClipboardList, ShieldCheck };

/* ─── Summary helper components (must be outside main component) ─── */
const SummaryItem = ({ label, value }) => (
  <div>
    <p className="text-[10px] font-sans font-semibold text-stone-400 uppercase tracking-wide">{label}</p>
    <p className="text-sm font-sans text-stone-700 mt-0.5">{value || '\u2014'}</p>
  </div>
);

const SummaryCard = ({ title, value }) => (
  <div className="flex items-center justify-between bg-white rounded-xl border border-stone-100 px-4 py-3">
    <span className="text-xs font-sans font-medium text-stone-600">{title}</span>
    <span className="text-xs font-sans font-bold text-stone-800">{value}</span>
  </div>
);

const TemplateCustomizer = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();

  const productType = searchParams.get('type') || 'memory-book';
  const mode = searchParams.get('mode') || 'customize';
  const prefillTopic = searchParams.get('topic');
  const productSlug = searchParams.get('product') || productType;
  const schema = getCustomizationSchema(productType);

  const isQuickMode = mode === 'quick';
  const isWedding = productType === 'wedding-planner';

  // ─── State ───
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [templateData, setTemplateData] = useState({});
  const [characters, setCharacters] = useState([{ ...defaultCharacter }]);
  const [selectedStyle, setSelectedStyle] = useState(schema?.styleOptions?.[0]?.id || 'watercolor');
  const [selectedPalette, setSelectedPalette] = useState(schema?.paletteOptions?.[0]?.id || 'soft-rainbow');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  // Preview mode state
  const [previewAccess, setPreviewAccess] = useState(null);
  const [previewProduct, setPreviewProduct] = useState(null);
  const [previewSteps, setPreviewSteps] = useState(3);
  const [showPaywall, setShowPaywall] = useState(false);
  const autosaveTimer = useRef(null);
  const draftLoaded = useRef(false);

  useEffect(() => { if (!authLoading && !user) navigate('/login'); }, [user, authLoading, navigate]);
  useEffect(() => { if (prefillTopic) setFormData(prev => ({ ...prev, title: decodeURIComponent(prefillTopic) })); }, [prefillTopic]);

  // ── Check product access (preview vs full) ──
  useEffect(() => {
    if (authLoading || !user || !productSlug) return;
    const checkAccess = async () => {
      try {
        const res = await axios.get(`${API}/purchases/preview-access/${productSlug}`);
        setPreviewAccess(res.data.access);
        setPreviewProduct(res.data.product);
        setPreviewSteps(res.data.product?.preview_steps || 3);
      } catch {
        setPreviewAccess('full');
      }
    };
    checkAccess();
  }, [productSlug, authLoading, user]);

  // ── Load saved draft ──
  useEffect(() => {
    if (!user || !productSlug || draftLoaded.current || authLoading) return;
    draftLoaded.current = true;
    const loadDraft = async () => {
      try {
        const res = await axios.get(`${API}/drafts/${productSlug}`);
        const draft = res.data?.draft;
        if (draft?.form_data && Object.keys(draft.form_data).length > 0) {
          setFormData(prev => ({ ...prev, ...draft.form_data }));
          if (draft.form_data._templateData) {
            setTemplateData(draft.form_data._templateData);
          }
          if (draft.form_data._characters) {
            setCharacters(draft.form_data._characters);
          }
          if (draft.form_data._selectedStyle) {
            setSelectedStyle(draft.form_data._selectedStyle);
          }
          if (draft.form_data._selectedPalette) {
            setSelectedPalette(draft.form_data._selectedPalette);
          }
          if (draft.step > 0) {
            setCurrentStep(draft.step);
          }
          toast('Welcome back', { description: 'We restored your saved progress.', icon: '✨' });
        }
      } catch {
        // No draft
      }
    };
    loadDraft();
  }, [user, productSlug, authLoading]);

  // ── Autosave draft (debounced) ──
  const saveDraft = useCallback(async () => {
    if (!user || !productSlug) return;
    try {
      await axios.post(`${API}/drafts/save`, {
        product_slug: productSlug,
        step: currentStep,
        form_data: {
          ...formData,
          _templateData: templateData,
          _characters: characters,
          _selectedStyle: selectedStyle,
          _selectedPalette: selectedPalette,
        },
      });
    } catch { /* silent */ }
  }, [user, productSlug, currentStep, formData, templateData, characters, selectedStyle, selectedPalette]);

  useEffect(() => {
    if (!productSlug) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(saveDraft, 2000);
    return () => clearTimeout(autosaveTimer.current);
  }, [formData, templateData, characters, selectedStyle, selectedPalette, currentStep, saveDraft, productSlug]);

  // ── Preview step gate ──
  const canProceedToStep = useCallback((targetStep) => {
    if (previewAccess === 'full' || !productSlug) return true;
    if (previewAccess === 'preview' && targetStep >= previewSteps) return false;
    return true;
  }, [previewAccess, previewSteps, productSlug]);

  const handleStepChange = useCallback((targetStep) => {
    if (!canProceedToStep(targetStep)) {
      setShowPaywall(true);
      return;
    }
    setShowPaywall(false);
    setCurrentStep(targetStep);
  }, [canProceedToStep]);

  const updateField = useCallback((id, value) => setFormData(prev => ({ ...prev, [id]: value })), []);
  const updateSection = useCallback((key, value) => setTemplateData(prev => ({ ...prev, [key]: value })), []);

  // ─── Cross-module computed data ───
  const guestCount = useMemo(() => {
    if (!isWedding) return 0;
    const listed = (templateData.guests || []).filter(g => g.rsvp !== 'declined').length;
    return listed > 0 ? listed : (parseInt(formData.guestCount) || 0);
  }, [isWedding, templateData.guests, formData.guestCount]);

  const hasChildren = useMemo(() => (templateData.guests || []).some(g => g.hasChildren), [templateData.guests]);

  const guestAllrgyes = useMemo(() => {
    const items = [];
    (templateData.guests || []).forEach(g => {
      if (g.allergies?.trim()) items.push(`${g.name || 'Guest'}: ${g.allergies}`);
      if (g.meal && g.meal !== 'standard') items.push(`${g.name || 'Guest'}: ${g.meal}`);
    });
    return items;
  }, [templateData.guests]);

  // ─── Visible sections (conditional logic) ───
  const computedFields = useMemo(() => ({
    _hasChildren: hasChildren,
  }), [hasChildren]);

  const visibleSections = useMemo(() => (schema?.sections || []).filter(section => {
    if (!section.conditional) return true;
    const field = section.conditional.field;
    const val = field.startsWith('_') ? computedFields[field] : formData[field];
    if (section.conditional.values) return section.conditional.values.includes(val);
    if (section.conditional.value !== undefined) return val === section.conditional.value;
    return true;
  }), [schema, formData, computedFields]);

  const wizardSteps = isQuickMode
    ? [{ id: 'quick', title: 'Hurtigvalg', icon: 'Sparkles' }]
    : [...visibleSections, { id: 'preview', title: 'Preview', icon: 'Eye' }];

  // Clamp step if sections changed (e.g. Plan B toggled off)
  useEffect(() => {
    if (currentStep >= wizardSteps.length) setCurrentStep(Math.max(0, wizardSteps.length - 1));
  }, [wizardSteps.length, currentStep]);

  const navigateToSection = (sectionId) => {
    const idx = wizardSteps.findIndex(s => s.id === sectionId);
    if (idx >= 0) setCurrentStep(idx);
  };

  const getStepIcon = (step) => iconMap[step.icon] || Eye;

  // ─── Can proceed validation ───
  const canProceed = () => {
    if (isQuickMode) {
      if (isWedding) return formData.person1Name?.trim() && formData.person2Name?.trim();
      return formData.title?.trim();
    }
    const step = wizardSteps[currentStep];
    if (!step) return true;
    if (step.id === 'content') return formData.title?.trim() && formData.theme;
    if (step.id === 'couple') return formData.person1Name?.trim() && formData.person2Name?.trim();
    if (step.id === 'design') return !!formData.bookTheme;
    if (step.id === 'basics') return formData.venueType;
    if (step.id === 'characters') return characters.length > 0 && characters[0]?.name?.trim();
    return true;
  };

  // ─── Character Bible (children's book) ───
  const buildCharacterBible = () => characters.map(char => {
    const bible = { ...char };
    delete bible.referenceImage;
    if (char.inclusiveFeatures) {
      bible.inclusiveFeatures = Object.entries(char.inclusiveFeatures)
        .filter(([, v]) => v?.enabled || (typeof v === 'string' && v))
        .map(([key, v]) => typeof v === 'string' ? `${key}: ${v}` : v.detail ? `${key}: ${v.detail}` : key);
    }
    return bible;
  });

  // ─── Generate / Save ───
  const handleGenerate = async () => {
    if (isWedding) {
      // For wedding, just navigate to preview for now
      if (isQuickMode) {
        navigate(`/customize?type=${productType}&mode=customize`, { replace: true });
      }
      return;
    }
    setError('');
    setGenerating(true);
    try {
      const styleMeta = illustrationStyles.find(s => s.id === selectedStyle);
      const paletteMeta = colorPalettes.find(p => p.id === selectedPalette);
      const payload = {
        productType, topic: formData.title || 'Childebok',
        audience: formData.ageRange ? `Child ${formData.ageRange} years` : 'Child 3-5 years',
        painPoint: '', tone: 'friendly', language: formData.language || 'no',
        length: formData.pageCount === '32' ? 'long' : formData.pageCount === '8' ? 'short' : 'medium',
        outputSize: 'a4-document', slideCount: parseInt(formData.pageCount || '16'), contentStructure: [],
        customization: {
          theme: formData.theme || 'adventure', customTheme: formData.customTheme,
          message: formData.message, ageRange: formData.ageRange || '3-5',
          pageCount: formData.pageCount || '16', illustrationStyle: selectedStyle,
          illustrationStyleName: styleMeta?.name, colorPalette: selectedPalette,
          colorPaletteColors: paletteMeta?.colors, characterBible: buildCharacterBible(),
          hasReferenceImages: characters.some(c => c.referenceImage),
        },
      };
      const startRes = await axios.post(`${API}/content/generate`, payload, { timeout: 15000 });
      const taskId = startRes.data.task_id;
      let attempts = 0;
      while (attempts < 60) {
        await new Promise(r => setTimeout(r, 3000));
        attempts++;
        try {
          const pollRes = await axios.get(`${API}/content/tasks/${taskId}?_t=${Date.now()}`, { timeout: 10000 });
          if (pollRes.data.status === 'complete') { navigate(`/generator?project_id=${pollRes.data.result?.project_id || ''}`); return; }
          if (pollRes.data.status === 'failed') { setError(pollRes.data.error || 'Generering feilet.'); setGenerating(false); return; }
        } catch (e) { if (e.response?.status === 404 && attempts < 5) continue; if (attempts >= 60) throw e; }
      }
      setError('Generering tok for lang tid.');
    } catch (err) { setError(err.response?.data?.detail || 'Generation failed. Please try again.'); }
    setGenerating(false);
  };

  if (!user || authLoading || !schema) return null;

  // ─── Field renderers ───
  const renderFieldInput = (field) => {
    switch (field.type) {
      case 'text':
        return <Input data-testid={`field-${field.id}`} value={formData[field.id] || ''} onChange={e => updateField(field.id, e.target.value)}
          placeholder={field.placeholder} className="bg-stone-50 border-stone-200 focus:border-primary rounded-xl" />;
      case 'textarea':
        return <Textarea data-testid={`field-${field.id}`} value={formData[field.id] || ''} onChange={e => updateField(field.id, e.target.value)}
          placeholder={field.placeholder} rows={3} className="bg-stone-50 border-stone-200 focus:border-primary rounded-xl" />;
      case 'number':
        return <Input data-testid={`field-${field.id}`} type="number" value={formData[field.id] || ''} onChange={e => updateField(field.id, e.target.value)}
          placeholder={field.placeholder} min={field.min} max={field.max} className="bg-stone-50 border-stone-200 focus:border-primary rounded-xl w-40" />;
      case 'date':
        return <Input data-testid={`field-${field.id}`} type="date" value={formData[field.id] || ''} onChange={e => updateField(field.id, e.target.value)}
          className="bg-stone-50 border-stone-200 focus:border-primary rounded-xl w-52" />;
      case 'select':
        return (
          <div className="flex flex-wrap gap-2">
            {field.options.map(opt => (
              <button key={opt.value} type="button" data-testid={`field-${field.id}-${opt.value}`}
                onClick={() => updateField(field.id, opt.value)}
                className={`text-xs font-sans px-3 py-2 rounded-xl border transition-all ${
                  formData[field.id] === opt.value ? 'bg-primary/10 border-primary text-primary font-semibold' : 'bg-white border-stone-200 text-stone-600 hover:border-stone-400'
                }`}>{opt.label}</button>
            ))}
          </div>
        );
      case 'multi-select': {
        const selected = formData[field.id] || [];
        return (
          <div>
            <div className="flex flex-wrap gap-2">
              {field.options.map(opt => {
                const on = selected.includes(opt.value);
                const atMax = field.maxSelections && selected.length >= field.maxSelections && !on;
                return (
                  <button key={opt.value} type="button" data-testid={`field-${field.id}-${opt.value}`} disabled={atMax}
                    onClick={() => updateField(field.id, on ? selected.filter(v => v !== opt.value) : [...selected, opt.value])}
                    className={`text-xs font-sans px-3 py-2 rounded-xl border transition-all ${
                      on ? 'bg-primary/10 border-primary text-primary font-semibold' :
                      atMax ? 'bg-stone-50 border-stone-100 text-stone-300 cursor-not-allowed' :
                      'bg-white border-stone-200 text-stone-600 hover:border-stone-400'
                    }`}>{on && <Check className="w-3 h-3 inline mr-1" />}{opt.label}</button>
                );
              })}
            </div>
            {field.maxSelections && <p className="text-[10px] font-sans text-stone-400 mt-1">{selected.length} / {field.maxSelections} selected</p>}
          </div>
        );
      }
      default: return null;
    }
  };

  // ─── Generic fields section ───
  const renderFieldsSection = (section) => (
    <div data-testid={`step-${section.id}`} className="animate-fade-in-up">
      <div className="bg-white rounded-2xl border border-stone-100 p-6 space-y-5">
        {section.fields.map(field => {
          if (field.conditional) {
            const val = formData[field.conditional.field];
            if (val !== field.conditional.value) return null;
          }
          return (
            <div key={field.id}>
              <label className="text-sm font-sans font-medium text-stone-700 mb-1.5 block">
                {field.label} {field.required && <span className="text-red-400">*</span>}
              </label>
              {renderFieldInput(field)}
            </div>
          );
        })}
      </div>
    </div>
  );

  // ─── Section renderer (type-based dispatch) ───
  const renderSectionContent = (section) => {
    if (section.fields) return renderFieldsSection(section);
    switch (section.type) {
      case 'couple-setup':
        return <div className="animate-fade-in-up"><CoupleSetup formData={formData} onChange={updateField} /></div>;
      case 'book-design-selector':
        return <div className="animate-fade-in-up"><BookDesignSelector selectedTheme={formData.bookTheme || ''} onSelectTheme={v => updateField('bookTheme', v)} formData={formData} onChange={updateField} /></div>;
      case 'character-builder':
        return <div className="animate-fade-in-up"><CharacterBuilder characters={characters} onChange={setCharacters} /></div>;
      case 'style-picker':
        return (
          <div className="animate-fade-in-up space-y-8">
            <StylePicker selectedStyle={selectedStyle} onChange={setSelectedStyle} styles={schema.styleOptions}
              title={isWedding ? 'Bryllupsstil' : undefined} subtitle={isWedding ? 'Choose the visual style for the wedding' : undefined} />
            {schema.paletteOptions && (
              <ColorPalettePicker selectedPalette={selectedPalette} onChange={setSelectedPalette}
                palettes={schema.paletteOptions} title={isWedding ? 'Fargepalett' : undefined} />
            )}
          </div>
        );
      case 'color-palette-picker':
        return <div className="animate-fade-in-up"><ColorPalettePicker selectedPalette={selectedPalette} onChange={setSelectedPalette} palettes={schema.paletteOptions} /></div>;
      case 'guest-list':
        return <div className="animate-fade-in-up"><GuestListBuilder guests={templateData.guests || []} onChange={v => updateSection('guests', v)} couple={isWedding ? formData : null} /></div>;
      case 'seating-chart':
        return <div className="animate-fade-in-up"><SeatingChart seating={templateData.seating || {}} onChange={v => updateSection('seating', v)} guests={templateData.guests || []} couple={isWedding ? formData : null} /></div>;
      case 'moodboard':
        return <div className="animate-fade-in-up"><MoodboardBuilder moodboard={templateData.moodboard || {}} onChange={v => updateSection('moodboard', v)} formData={isWedding ? formData : null} /></div>;
      case 'flower-planner':
        return <div className="animate-fade-in-up"><FlowerPlanner flowers={templateData.flowers || {}} onChange={v => updateSection('flowers', v)} formData={isWedding ? formData : null} /></div>;
      case 'stationery-planner':
        return <div className="animate-fade-in-up"><StationeryPlanner stationery={templateData.stationery || {}} onChange={v => updateSection('stationery', v)} formData={isWedding ? formData : null} /></div>;
      case 'attire-planner':
        return <div className="animate-fade-in-up"><AttirePlanner attire={templateData.attire || {}} onChange={v => updateSection('attire', v)} couple={isWedding ? formData : null} /></div>;
      case 'photo-checklist':
        return <div className="animate-fade-in-up"><PhotoChecklist photos={templateData.photos || {}} onChange={v => updateSection('photos', v)} guests={templateData.guests || []} couple={isWedding ? formData : null} memorials={templateData.memorials || []} /></div>;
      case 'food-planner':
        return <div className="animate-fade-in-up"><FoodPlanner food={templateData.food || {}} onChange={v => updateSection('food', v)} hasChildren={hasChildren} hasAlcohol={formData.hasAlcohol} guestAllrgyes={guestAllrgyes} /></div>;
      case 'budget-tracker':
        return <div className="animate-fade-in-up"><BudgetTracker budget={templateData.budget || { totalBudget: formData.totalBudget || '', categories: {} }} onChange={v => updateSection('budget', v)} guestCount={guestCount} templateData={templateData} /></div>;
      case 'music-planner':
        return <div className="animate-fade-in-up"><MusicPlanner music={templateData.music || { playlists: {} }} onChange={v => updateSection('music', v)} couple={isWedding ? formData : null} /></div>;
      case 'vendor-list':
        return <div className="animate-fade-in-up"><VendorList vendors={templateData.vendors || []} onChange={v => updateSection('vendors', v)} /></div>;
      case 'wedding-timeline':
        return <div className="animate-fade-in-up"><WeddingTimeline timeline={templateData.timeline || {}} onChange={v => updateSection('timeline', v)} formData={formData} templateData={templateData} /></div>;
      case 'ceremony-planner':
        return <div className="animate-fade-in-up"><CeremonyPlanner ceremony={templateData.ceremony || {}} onChange={v => updateSection('ceremony', v)} couple={isWedding ? formData : null} /></div>;
      case 'kids-planner':
        return <div className="animate-fade-in-up"><KidsPlanner kids={templateData.kids || {}} onChange={v => updateSection('kids', v)} guests={templateData.guests || []} /></div>;
      case 'transport-planner':
        return <div className="animate-fade-in-up"><TransportPlanner transport={templateData.transport || {}} onChange={v => updateSection('transport', v)} guests={templateData.guests || []} /></div>;
      case 'responsibility-tracker':
        return <div className="animate-fade-in-up"><ResponsibilityTracker responsibilities={templateData.responsibilities || {}} onChange={v => updateSection('responsibilities', v)} /></div>;
      case 'emergency-kit':
        return <div className="animate-fade-in-up"><EmergencyKit kit={templateData.emergency || {}} onChange={v => updateSection('emergency', v)} /></div>;
      case 'memorial':
        return <div className="animate-fade-in-up"><MemorialSection memorials={templateData.memorials || []} onChange={v => updateSection('memorials', v)} /></div>;
      case 'plan-b':
        return <div className="animate-fade-in-up"><PlanBSection planB={templateData.planB || {}} onChange={v => updateSection('planB', v)} /></div>;
      default: return null;
    }
  };

  // ─── Preview ───
  const renderPreview = () => {
    if (isWedding) return renderWeddingPreview();
    return renderBookPreview();
  };

  const renderBookPreview = () => {
    const styleMeta = (schema.styleOptions || illustrationStyles).find(s => s.id === selectedStyle);
    const paletteMeta = (schema.paletteOptions || colorPalettes).find(p => p.id === selectedPalette);
    const mainChar = characters.find(c => c.role === 'main') || characters[0];
    return (
      <div data-testid="step-preview" className="animate-fade-in-up">
        <div className="bg-white rounded-2xl border border-stone-100 p-6 space-y-6">
          <h3 className="font-sans font-semibold text-stone-800 text-base">Oppsummering</h3>
          <div className="grid grid-cols-2 gap-4">
            <SummaryItem label="Tittel" value={formData.title} />
            <SummaryItem label="Tema" value={schema.sections[0]?.fields?.find(f => f.id === 'theme')?.options?.find(o => o.value === formData.theme)?.label} />
            <SummaryItem label="Aldersgruppe" value={formData.ageRange} />
            <SummaryItem label="Sider" value={formData.pageCount} />
          </div>
          <div>
            <p className="text-xs font-sans font-semibold text-stone-500 uppercase tracking-wide mb-2">Karakterer ({characters.length})</p>
            <div className="flex flex-wrap gap-2">
              {characters.map((char, i) => (
                <div key={i} className="flex items-center gap-2 bg-stone-50 rounded-xl px-3 py-2 border border-stone-200">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-sans font-bold text-primary">
                    {char.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <span className="text-xs font-sans text-stone-700">{char.name || `Karakter ${i + 1}`}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-4">
            {styleMeta && (
              <div className="flex items-center gap-3 flex-1">
                <img src={styleMeta.thumbnail} alt="" className="w-12 h-12 rounded-xl object-cover" />
                <div><p className="text-[10px] font-sans text-stone-400 uppercase">Stil</p><p className="text-sm font-sans font-medium text-stone-700">{styleMeta.name}</p></div>
              </div>
            )}
            {paletteMeta && (
              <div className="flex items-center gap-3 flex-1">
                <div className="flex -space-x-1">{paletteMeta.colors.slice(0, 4).map((c, i) => <div key={i} className="w-6 h-6 rounded-full border-2 border-white" style={{ backgroundColor: c }} />)}</div>
                <div><p className="text-[10px] font-sans text-stone-400 uppercase">Farger</p><p className="text-sm font-sans font-medium text-stone-700">{paletteMeta.name}</p></div>
              </div>
            )}
          </div>
          {mainChar?.referenceImage && (
            <div className="flex items-center gap-3 bg-stone-50 rounded-xl p-3 border border-stone-200">
              <img src={mainChar.referenceImage} alt="" className="w-10 h-10 rounded-lg object-cover" />
              <p className="text-xs font-sans text-stone-600">Referansebilde lastet opp for {mainChar.name}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderWeddingPreview = () => {
    const venueLabel = { indoor: 'Indoor', outdoor: 'Outdoor', mixed: 'Mixed' }[formData.venueType] || '\u2014';
    const budgetVal = templateData.budget?.totalBudget || formData.totalBudget;
    const guestsArr = templateData.guests || [];
    const vendorsArr = templateData.vendors || [];
    const foodCourses = Object.values(templateData.food?.courses || {}).reduce((s, d) => s + d.length, 0);
    const coupleNames = getCoupleLabel(formData);
    const theme = getThemeById(formData.bookTheme);

    return (
      <div data-testid="step-preview" className="animate-fade-in-up space-y-6">
        {/* Book design showcase */}
        {theme && (
          <div className="rounded-2xl overflow-hidden border-2" style={{ borderColor: theme.colors[1] + '30' }}>
            <div className="flex flex-col md:flex-row">
              <img src={theme.coverImage} alt={theme.name} className="w-full md:w-48 aspect-square md:aspect-auto object-cover" />
              <div className="flex-1 p-5" style={{ backgroundColor: theme.colors[4] || theme.colors[0] }}>
                <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: theme.colors[1], fontFamily: theme.fonts.body }}>{theme.name}</p>
                <p className="text-2xl font-bold mb-1" style={{ color: theme.colors[3], fontFamily: theme.fonts.heading }}>{coupleNames}</p>
                {formData.weddingDate && <p className="text-sm" style={{ color: theme.colors[2], fontFamily: theme.fonts.body }}>{formData.weddingDate}</p>}
                {formData.venueName && <p className="text-xs mt-1" style={{ color: theme.colors[2], fontFamily: theme.fonts.body }}>{formData.venueName}</p>}
                <div className="flex gap-1 mt-3">
                  {theme.colors.map((c, i) => <div key={i} className="w-5 h-5 rounded-full border border-white/40" style={{ backgroundColor: c }} />)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Couple info */}
        <div className="bg-white rounded-2xl border border-stone-100 p-6">
          <h3 className="font-sans font-semibold text-stone-800 text-base mb-4">Oppsummering</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <SummaryItem label={getPersonTitle(formData, 1)} value={formData.person1Name} />
            <SummaryItem label={getPersonTitle(formData, 2)} value={formData.person2Name} />
            <SummaryItem label="Date" value={formData.weddingDate} />
            <SummaryItem label="Sted" value={formData.venueName} />
            <SummaryItem label="Guester" value={guestCount > 0 ? `${guestCount} (${guestsArr.filter(g => g.rsvp === 'confirmed').length} bekreftet)` : formData.guestCount} />
            <SummaryItem label="Budget" value={budgetVal ? `${parseInt(budgetVal).toLocaleString('en-US')} kr` : undefined} />
            <SummaryItem label="Type" value={venueLabel} />
            <SummaryItem label="Design" value={theme?.name} />
          </div>
        </div>

        {guestsArr.length > 0 && <SummaryCard title="Guester" value={`${guestsArr.length} registered`} />}
        {foodCourses > 0 && <SummaryCard title="Mat & Drikke" value={`${foodCourses} retter planned`} />}
        {vendorsArr.length > 0 && <SummaryCard title="Vendors" value={`${vendorsArr.length} registered`} />}
        {(templateData.memorials || []).length > 0 && <SummaryCard title="Those we carry with us" value={`${templateData.memorials.length} personer`} />}

        {/* Forgotten Analyzer */}
        <ForgottenAnalyzer formData={formData} templateData={templateData} onNavigateToSection={navigateToSection} />
      </div>
    );
  };

  // ─── Quick Mode ───
  const renderQuickMode = () => (
    <div data-testid="quick-mode" className="animate-fade-in-up space-y-6">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-7 h-7 text-primary" />
        </div>
        <h2 className="text-2xl font-serif font-bold text-stone-900 mb-2">Create for me</h2>
        <p className="text-sm font-sans text-stone-500 max-w-md mx-auto">
          {isWedding
            ? 'Answer the most important questions, and Lumera helps you with the rest.'
            : 'Answer a few questions, and Lumera makes smart choices for the rest.'}
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-stone-100 p-6 space-y-5 max-w-xl mx-auto">
        {schema.quickFields.map(fieldId => {
          // Special fields
          if (fieldId === 'childName') return (
            <div key={fieldId}>
              <label className="text-sm font-sans font-medium text-stone-700 mb-1.5 block">Childets navn <span className="text-red-400">*</span></label>
              <Input data-testid="quick-child-name" value={characters[0]?.name || ''}
                onChange={e => { const u = [...characters]; if (!u.length) u.push({ ...defaultCharacter }); u[0] = { ...u[0], name: e.target.value }; setCharacters(u); }}
                placeholder="Hva heter barnet?" className="bg-stone-50 border-stone-200 focus:border-primary rounded-xl" />
            </div>
          );
          if (fieldId === 'childAge') return (
            <div key={fieldId}>
              <label className="text-sm font-sans font-medium text-stone-700 mb-1.5 block">Childets alder</label>
              <Input data-testid="quick-child-age" type="number" min="0" max="15" value={characters[0]?.age || ''}
                onChange={e => { const u = [...characters]; if (!u.length) u.push({ ...defaultCharacter }); u[0] = { ...u[0], age: e.target.value }; setCharacters(u); }}
                placeholder="e.g. 5" className="bg-stone-50 border-stone-200 focus:border-primary rounded-xl w-32" />
            </div>
          );
          if (fieldId === 'illustrationStyle') return (
            <div key={fieldId}>
              <label className="text-sm font-sans font-medium text-stone-700 mb-2 block">Choose stil</label>
              <div className="grid grid-cols-4 gap-2">
                {(schema.styleOptions || illustrationStyles).slice(0, 4).map(style => (
                  <button key={style.id} type="button" data-testid={`quick-style-${style.id}`} onClick={() => setSelectedStyle(style.id)}
                    className={`rounded-xl overflow-hidden border-2 transition-all ${selectedStyle === style.id ? 'border-primary ring-1 ring-primary/20' : 'border-stone-200'}`}>
                    <img src={style.thumbnail} alt={style.name} className="w-full aspect-square object-cover" loading="lazy" />
                    <p className="text-[10px] font-sans text-center py-1 text-stone-600">{style.name}</p>
                  </button>
                ))}
              </div>
            </div>
          );
          if (fieldId === 'totalBudget') return (
            <div key={fieldId}>
              <label className="text-sm font-sans font-medium text-stone-700 mb-1.5 block">Totalbudget (kr)</label>
              <Input data-testid="quick-totalBudget" type="number" value={formData.totalBudget || ''} onChange={e => updateField('totalBudget', e.target.value)}
                placeholder="e.g. 350000" className="bg-stone-50 border-stone-200 focus:border-primary rounded-xl w-48" />
            </div>
          );
          if (fieldId === 'person1Name') return (
            <div key={fieldId}>
              <label className="text-sm font-sans font-medium text-stone-700 mb-1.5 block">Person 1 – Navn <span className="text-red-400">*</span></label>
              <Input data-testid="quick-person1" value={formData.person1Name || ''} onChange={e => updateField('person1Name', e.target.value)}
                placeholder="e.g. Anna" className="bg-stone-50 border-stone-200 focus:border-primary rounded-xl" />
            </div>
          );
          if (fieldId === 'person2Name') return (
            <div key={fieldId}>
              <label className="text-sm font-sans font-medium text-stone-700 mb-1.5 block">Person 2 – Navn <span className="text-red-400">*</span></label>
              <Input data-testid="quick-person2" value={formData.person2Name || ''} onChange={e => updateField('person2Name', e.target.value)}
                placeholder="e.g. Erik" className="bg-stone-50 border-stone-200 focus:border-primary rounded-xl" />
            </div>
          );
          if (fieldId === 'bookTheme') {
            return (
              <div key={fieldId}>
                <label className="text-sm font-sans font-medium text-stone-700 mb-2 block">Choose design</label>
                <div className="grid grid-cols-3 gap-2">
                  {allWeddingThemes.slice(0, 6).map(theme => (
                    <button key={theme.id} type="button" data-testid={`quick-theme-${theme.id}`} onClick={() => updateField('bookTheme', theme.id)}
                      className={`rounded-xl overflow-hidden border-2 transition-all ${formData.bookTheme === theme.id ? 'border-primary ring-1 ring-primary/20' : 'border-stone-200'}`}>
                      <img src={theme.coverImage} alt={theme.name} className="w-full aspect-square object-cover" loading="lazy" />
                      <p className="text-[10px] font-sans text-center py-1 text-stone-600">{theme.name}</p>
                    </button>
                  ))}
                </div>
              </div>
            );
          }
          if (fieldId === 'weddingStyle') return (
            <div key={fieldId}>
              <label className="text-sm font-sans font-medium text-stone-700 mb-2 block">Choose stil</label>
              <div className="grid grid-cols-3 gap-2">
                {(schema.styleOptions || []).slice(0, 6).map(style => (
                  <button key={style.id} type="button" data-testid={`quick-style-${style.id}`} onClick={() => setSelectedStyle(style.id)}
                    className={`rounded-xl overflow-hidden border-2 transition-all ${selectedStyle === style.id ? 'border-primary ring-1 ring-primary/20' : 'border-stone-200'}`}>
                    <img src={style.thumbnail} alt={style.name} className="w-full aspect-[4/3] object-cover" loading="lazy" />
                    <p className="text-[10px] font-sans text-center py-1 text-stone-600">{style.name}</p>
                  </button>
                ))}
              </div>
            </div>
          );
          // Generic: find field in schema sections
          const section = schema.sections.find(s => s.fields?.some(f => f.id === fieldId));
          const field = section?.fields?.find(f => f.id === fieldId);
          if (!field) return null;
          return (
            <div key={fieldId}>
              <label className="text-sm font-sans font-medium text-stone-700 mb-1.5 block">
                {field.label} {field.required && <span className="text-red-400">*</span>}
              </label>
              {renderFieldInput(field)}
            </div>
          );
        })}
      </div>

      <div className="text-center mt-4">
        <button type="button" onClick={() => navigate(`/customize?type=${productType}&mode=customize`, { replace: true })}
          className="text-xs font-sans text-stone-500 hover:text-primary transition-colors underline underline-offset-2">
          Want to customize more? Switch to full customization
        </button>
      </div>
    </div>
  );

  // ─── Main render dispatch ───
  const renderCurrentStep = () => {
    if (isQuickMode) return renderQuickMode();
    const step = wizardSteps[currentStep];
    if (!step) return null;
    if (step.id === 'preview') return renderPreview();
    return renderSectionContent(step);
  };

  // ─── Button labels ───
  const isLastStep = isQuickMode || currentStep === wizardSteps.length - 1;
  const generateLabel = isWedding
    ? (isQuickMode ? 'Til full tilpasning' : 'Done')
    : (generating ? 'Genererer...' : 'Generer boken');

  return (
    <div data-testid="template-customizer" className="min-h-screen bg-[#F7F5F2] dark:bg-background">
      <nav className="bg-white dark:bg-card border-b border-[#d8cfc8]/50 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between h-14">
          <LumeraLogo size="default" />
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/generator')} className="text-stone-600 font-sans text-xs">
              <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back
            </Button>
            <UserNavMenu size={32} />
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-8">
          <p className="text-xs font-sans font-medium text-primary uppercase tracking-wide mb-1">{schema.name}</p>
          <h1 className="text-2xl md:text-3xl font-serif font-bold text-stone-900">
            {isQuickMode ? 'Create for me' : 'Tilpass selv'}
          </h1>
        </div>

        {/* Preview step indicator */}
        {previewAccess === 'preview' && !showPaywall && !isQuickMode && (
          <div data-testid="customizer-preview-indicator" className="flex items-center justify-center gap-2 mb-4">
            <Eye className="w-3.5 h-3.5 text-amber-600" />
            <span className="text-xs font-sans font-medium text-amber-700 bg-amber-50 px-3 py-1 rounded-full">
              Free preview · Step {Math.min(currentStep + 1, previewSteps)} of {previewSteps}
            </span>
          </div>
        )}

        {/* Premium Paywall */}
        {showPaywall && (
          <div className="py-8">
            <PremiumPaywall
              product={previewProduct}
              productSlug={productSlug}
              previewStep={currentStep}
              totalPreviewSteps={previewSteps}
            />
          </div>
        )}

        {/* Step indicators */}
        {!isQuickMode && (
          <div className="flex items-center gap-1.5 mb-8 overflow-x-auto pb-2">
            {wizardSteps.map((step, i) => {
              const Icon = getStepIcon(step);
              const isLocked = previewAccess === 'preview' && i >= previewSteps;
              return (
                <React.Fragment key={step.id}>
                  <button onClick={() => { if (i <= currentStep && !isLocked) handleStepChange(i); }} disabled={i > currentStep || isLocked}
                    data-testid={`step-indicator-${step.id}`}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-sans font-semibold transition-all whitespace-nowrap ${
                      isLocked ? 'bg-stone-100 text-stone-300 cursor-not-allowed' :
                      i === currentStep ? 'bg-primary text-white shadow-md' :
                      i < currentStep ? 'bg-primary/10 text-primary cursor-pointer hover:bg-primary/20' :
                      'bg-stone-100 text-stone-400'
                    }`}>
                    {isLocked ? <Lock className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                    <span className="hidden sm:inline">{step.title}</span>
                  </button>
                  {i < wizardSteps.length - 1 && <div className={`w-6 h-0.5 flex-shrink-0 ${i < currentStep ? 'bg-primary/30' : 'bg-stone-200'}`} />}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {error && !showPaywall && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm font-sans rounded-xl px-4 py-3 mb-6">{error}</div>
        )}

        {!showPaywall && renderCurrentStep()}

        {/* Navigation */}
        {!showPaywall && (
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-stone-200">
          {!isQuickMode && currentStep > 0 ? (
            <Button variant="ghost" onClick={() => handleStepChange(currentStep - 1)} className="font-sans font-medium text-stone-600">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          ) : <div />}

          {isLastStep ? (
            <Button data-testid="generate-btn" onClick={handleGenerate} disabled={generating || !canProceed()}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-5 font-sans font-semibold shadow-lg">
              {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Genererer...</> : <><Sparkles className="w-4 h-4 mr-2" /> {generateLabel}</>}
            </Button>
          ) : (
            <Button data-testid="next-step-btn" onClick={() => handleStepChange(currentStep + 1)} disabled={!canProceed()}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 font-sans font-semibold shadow-lg">
              Next <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default TemplateCustomizer;
